import Immutable, { List, Map } from 'immutable';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';

// components
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';

import Comment from '../Comment';
import Value from '../Value';
import AssignVoteModal from '../AssignVoteModal';

// styles
import './ListItem.scss';

class ListItem extends Component {
  static propTypes = {
    allowAnonymousVoting: PropTypes.bool,
    authorName:           PropTypes.string.isRequired,
    categories:           PropTypes.instanceOf(Map),
    classes:              PropTypes.instanceOf(Object).isRequired,
    clearanceLevel:       PropTypes.number.isRequired,
    comment:              PropTypes.string,
    currentUserId:        PropTypes.number.isRequired,
    first:                PropTypes.bool,
    id:                   PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isConcernChair:       PropTypes.bool,
    item:                 PropTypes.instanceOf(Map).isRequired,
    last:                 PropTypes.bool,
    navigate:             PropTypes.func.isRequired,
    onDelete:             PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    pnmId:                PropTypes.string.isRequired,
    pnmName:              PropTypes.string.isRequired,
    pnmPhoto:             PropTypes.string.isRequired,
    renderTimeline:       PropTypes.bool,
    rounds:               PropTypes.instanceOf(List).isRequired,
    showPNM:              PropTypes.bool,
    tags:                 PropTypes.instanceOf(Array),
    teamLevel:            PropTypes.number.isRequired,
    type:                 PropTypes.oneOf(['votes', 'notes', 'matches', 'tags', 'concerns']).isRequired,
    updatedAt:            PropTypes.string.isRequired,
    value:                PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  static defaultProps = {
    allowAnonymousVoting: false,
    categories:           Map(),
    comment:              '',
    first:                false,
    isConcernChair:       false,
    last:                 false,
    onDelete:             false,
    renderTimeline:       true,
    showPNM:              false,
    tags:                 Immutable.List(),
    value:                '',
  };

  constructor(props) {
    super(props);

    this.state = {
      data: Immutable.Map({
        displayAssignVoteModal: false,
        hover:                  false,
        popupCategories:        null,
        valueHover:             false,
      }),
    };
  }

  setValueHover = () => {
    const { categories, item, type } = this.props;
    const { data } = this.state;

    // Check if the item is a vote and has categories
    if (type === 'votes' && item.get('categories')?.size > 0) {
      const itemCategories = item.get('categories');

      // Extract category values and names
      const categoryValues = itemCategories
        .map((category) => {
          const categoryId = category.get('category');
          const categoryName = categories.getIn([categoryId, 'name']) || 'Unknown category';

          return {
            name:  categoryName,
            value: category.get('value') || 'N/A',
          };
        })
        .toJS();

      this.setState({
        data: data.withMutations((map) => {
          map.set('hover', true);
          map.set('valueHover', true);
          map.set('popupCategories', categoryValues);
        }),
      });
    } else {
      this.setState({
        data: data.withMutations((map) => {
          map.set('hover', true);
          map.set('valueHover', true);
        }),
      });
    }
  };

  removeValueHover = (e) => {
    // Check if the new element the mouse entered is inside the parent
    const { parentNode } = e.currentTarget;
    if (parentNode && parentNode.contains(e.relatedTarget)) {
      // Mouse is still inside the parent, don't set hover to false
      this.setState({
        data: this.state.data.withMutations((map) => {
          map.set('valueHover', false);
          map.set('popupCategories', null);
        }),
      });
      return;
    }

    // Mouse left the parent, safe to set hover to false
    this.setState({
      data: this.state.data.withMutations((map) => {
        map.set('hover', false);
        map.set('valueHover', false);
        map.set('popupCategories', null);
      }),
    });
  };

  setHover = () => {
    this.setState({ data: this.state.data.set('hover', true) });
  };

  removeHover = () => {
    if (!this.state.data.get('valueHover')) {
      this.setState({ data: this.state.data.set('hover', false) });
    }
  };

  renderVoteCategoriesPopup = () => {
    const popupCategories = this.state.data.get('popupCategories');
    if (!popupCategories) return null;

    const { classes } = this.props;

    return (
      <div className={ classes.popupOverlay }>
        <Card className={ classes.popupCard } elevation={ 8 }>
          <CardContent className={ classes.popupContent }>
            <Typography variant='subtitle1'
              color='primary'
              className={ classes.popupTitle }>
              Category Breakdown
            </Typography>
            {popupCategories.map((category) => (
              <div key={ category.id } className={ classes.categoryRow }>
                <Typography variant='body2'
                  color='textSecondary'
                  style={ { lineHeight: 1 } }>
                  {category.name}
                </Typography>
                <Typography variant='h6'
                  color='primary'
                  className={ classes.categoryValue }
                  style={ { lineHeight: 1 } }>
                  {category.value}
                </Typography>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  handleDelete = () => {
    const { onDelete, id } = this.props;

    onDelete(id);
  };

  handleOpenAssignVoteModal = () => {
    this.setState({
      data: this.state.data.set('displayAssignVoteModal', true),
    });
  };

  handleCloseAssignVoteModal = () => {
    this.setState({
      data: this.state.data.set('displayAssignVoteModal', false),
    });
  };

  renderDeleteButton = () => {
    const { classes, onDelete } = this.props;

    let element;

    if (this.state.data.get('hover') && onDelete) {
      element = (
        <div className='comment-list-item__delete-container'>
          <IconButton className={ classes.deleteButton }
            onClick={ this.handleDelete }>
            <Icon color='action'>delete</Icon>
          </IconButton>
        </div>
      );
    }

    return element;
  };

  render() {
    const {
      allowAnonymousVoting,
      authorName,
      classes,
      clearanceLevel,
      comment,
      currentUserId,
      first,
      id,
      item,
      isConcernChair,
      last,
      navigate,
      pnmName,
      pnmId,
      pnmPhoto,
      renderTimeline,
      rounds,
      showPNM,
      tags,
      teamLevel,
      type,
      updatedAt,
      value,
    } = this.props;

    const displayAssignVoteModal = this.state.data.get(
      'displayAssignVoteModal'
    );

    const extraClassNames = {
      'comment-list-item__timeline--first': first,
      'comment-list-item__timeline--last':  last,
    };

    return (
      <Grid container
        className={ classes.voteListItemContainer }
        onMouseEnter={ this.setHover }
        onMouseLeave={ this.removeHover }>
        <Hidden smDown>
          {((item.get('author') || Map()).get('id') === currentUserId
            || (type === 'concerns' ? isConcernChair : teamLevel > 1) || clearanceLevel > 0)
            && this.renderDeleteButton()}
          {renderTimeline && (
            <div className='comment-list-item__timeline-container'>
              <div className={ classNames(
                'comment-list-item__timeline',
                extraClassNames
              ) } />
            </div>
          )}
        </Hidden>

        <Hidden xsDown>
          <Grid item
            sm={ 3 }
            md={ 4 }
            onMouseEnter={ this.setValueHover }
            onMouseLeave={ this.removeValueHover }>
            <Value type={ type }
              tags={ tags }
              value={ value }
              item={ item }
              rounds={ rounds }
              teamLevel={ teamLevel }
              clearanceLevel={ clearanceLevel }
              isConcernChair={ isConcernChair } />
            {type === 'votes' && this.renderVoteCategoriesPopup()}
          </Grid>
        </Hidden>
        <Hidden xsDown>
          <Grid item sm={ 9 } md={ 8 }>
            <Comment authorName={ authorName }
              handleOpenModal={ this.handleOpenAssignVoteModal }
              text={ comment }
              showPNM={ showPNM }
              pnmName={ pnmName }
              pnmId={ pnmId }
              pnmPhoto={ pnmPhoto }
              item={ item || Map() }
              navigate={ navigate }
              type={ type }
              rounds={ rounds }
              updatedAt={ updatedAt } />
          </Grid>
        </Hidden>

        <Hidden smUp>
          <Grid item xs={ 3 }>
            <Value type={ type }
              tags={ tags }
              value={ value }
              teamLevel={ teamLevel }
              clearanceLevel={ clearanceLevel } />
          </Grid>

          <Grid item xs={ 9 }>
            <Comment authorName={ authorName }
              handleOpenModal={ this.handleOpenAssignVoteModal }
              text={ comment }
              showPNM={ showPNM }
              pnmName={ pnmName }
              pnmId={ pnmId }
              pnmPhoto={ pnmPhoto }
              item={ item }
              rounds={ rounds }
              navigate={ navigate }
              updatedAt={ updatedAt } />
          </Grid>
        </Hidden>

        {type === 'votes' && allowAnonymousVoting && (
          <AssignVoteModal open={ displayAssignVoteModal }
            voteId={ id }
            item={ item }
            onClose={ this.handleCloseAssignVoteModal } />
        )}
      </Grid>
    );
  }
}

const styles = () => ({
  voteListItemContainer: {
    marginBottom: '1em',
    position:     'relative',
  },

  deleteButton: {
    position: 'absolute',
    left:     '-13px',
    zIndex:   1,
  },

  popupOverlay: {
    position:      'absolute',
    top:           '50%',
    left:          '30%',
    transform:     'translateY(-50%)',
    zIndex:        1000,
    pointerEvents: 'none',
    marginLeft:    '8px',
  },

  popupCard: {
    minWidth:  '200px',
    border:    '1px solid rgba(0, 0, 0, 0.12)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  },

  popupContent: { padding: '16px !important' },

  popupTitle: { marginBottom: '12px' },

  categoryRow: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    marginBottom:   '8px',
    '&:last-child': {
      marginBottom: 0,
    },
  },

  categoryValue: { marginLeft: '16px' },
});

export default withStyles(styles)(ListItem);
